import React, { useRef } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CopyButton } from '@backrs_ui/grow-ui/src/components/clipboard/CopyButton';
import type {
  StudyPlanPayload,
  StudyPlanWeek,
} from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';

import {
  Box,
  Typography,
  SxProps,
  Theme,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';

const STUDY_PLAN_STRINGS = appStrings.modules.officeHours.infoBox.studyPlan;

type DayKey = keyof typeof STUDY_PLAN_STRINGS.daysOfWeek;

const DAYS: DayKey[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

type WeekCardProps = Readonly<{
  week: StudyPlanWeek;
}>;

const WeekCard = ({ week }: WeekCardProps) => {
  const dayEntries = DAYS.map(
    (day) => [day, week[day] as readonly string[] | undefined] as const,
  ).filter(([, plans]) => plans && plans.length > 0);

  const rows: (readonly [DayKey, readonly string[] | undefined])[][] = [];

  for (let i = 0; i < dayEntries.length; i += 2) {
    rows.push(dayEntries.slice(i, i + 2));
  }

  return (
    <Box sx={weekDetailContainerSx}>
      {week.goals && week.goals.length > 0 && (
        <Box sx={goalsSectionSx}>
          <Typography variant="text-md-semibold" sx={sectionLabelSx}>
            {STUDY_PLAN_STRINGS.goals}
          </Typography>
          <Box sx={goalsListSx}>
            {week.goals.map((goal, index) => (
              <Typography
                key={index}
                variant="text-sm-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
              >
                • {goal}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      <Table sx={tableSx}>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map(([day, plans]) => (
                <TableCell key={day} sx={dayCellSx}>
                  <Typography variant="text-sm-semibold" sx={dayTitleSx}>
                    {STUDY_PLAN_STRINGS.daysOfWeek[day]}
                  </Typography>
                  <Box sx={dayActivitiesListSx}>
                    {(plans ?? []).map((plan, index) => (
                      <Typography key={index} variant="text-sm-regular" sx={dayActivityItemSx}>
                        • {plan}
                      </Typography>
                    ))}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

type StudyPlanRendererProps = Readonly<{
  payload: StudyPlanPayload;
}>;

export const StudyPlanRenderer = ({ payload }: StudyPlanRendererProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const hasPriorities = payload.priorities.length > 0;

  return (
    <Box sx={rootSx}>
      <Box sx={copyButtonRowSx}>
        <CopyButton id="study-plan-copy-button" contentRef={contentRef} />
      </Box>
      <Box ref={contentRef}>
        {hasPriorities && (
          <Box sx={summaryContainerSx}>
            <Box sx={columnContainerSx}>
              <Typography
                variant="text-lg-semibold"
                color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
              >
                {STUDY_PLAN_STRINGS.priorities}
              </Typography>
              <Box sx={summaryListSx}>
                {payload.priorities.map((priority, i) => (
                  <Typography
                    key={i}
                    variant="text-sm-regular"
                    color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
                  >
                    {i + 1}. {priority}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {payload.weeks.map((week) => (
          <React.Fragment key={week.week}>
            <Box sx={categoryDetailContainerSx}>
              <Box sx={categoryDetailHeaderContainerSx}>
                <Typography
                  variant="text-sm-semibold"
                  color={({ backrs }) => backrs.colors['Colors/Text/text-quaternary']}
                >
                  {STUDY_PLAN_STRINGS.week} {week.week}
                </Typography>
                <Divider sx={dividerSx} />
              </Box>
              <WeekCard week={week} />
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  py: backrs.spacing['spacing-md'],
});

const copyButtonRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: backrs.spacing['spacing-sm'],
});

const summaryContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-4xl'],
});

const columnContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const summaryListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const categoryDetailContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-3xl'],
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const categoryDetailHeaderContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  my: backrs.spacing['spacing-md'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-secondary'],
});

const weekDetailContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const tableSx: SxProps<Theme> = {
  width: '100%',
  borderCollapse: 'collapse',
};

const dayCellSx: SxProps<Theme> = ({ backrs }) => ({
  verticalAlign: 'top',
  paddingRight: backrs.spacing['spacing-xl'],
  paddingBottom: backrs.spacing['spacing-lg'],
  paddingLeft: 0,
  paddingTop: 0,
  border: 'none',
  width: '50%',
});

const goalsSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const sectionLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const goalsListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const dayTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const dayActivitiesListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const dayActivityItemSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});
